import React, { useState, useEffect, useRef } from "react";
import {
  FaPlus,
  FaInfoCircle,
} from "react-icons/fa";
import { FiCheck, FiTrash2, FiMinus, FiPlus } from "react-icons/fi";
import PetServiceCategoryDrawer from "../Orders/PetServiceCategoryDrawer";

// Formats an ISO date string (or any parseable date) to dd/mm/yyyy
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const ServiceDetailsCard = ({
  serviceCategory,
  serviceDetails,
  staffName,
  boardingOrderItem,
  addons,
  products,
  otherCharges,
  onOpenProductsDrawer,
  onOpenAddonsDrawer,
  onOpenOtherChargesDrawer,
  onDeleteAddon,
  onDeleteProduct,
  onIncrementProduct,
  onDecrementProduct,
  onDeleteOtherCharge,

  // ── New: everything needed to self-host the edit drawer ────────────────
  rawData, // the raw appointment/order row (ref_id, service_id, pet_type_id, etc.)
  orderId,
  clientId,
  clientName,
  clientPhone,
  vendorPetId,
  onServiceUpdated, // (updated) => void — parent syncs its serviceDetails/petDetails state
  readOnly = false,
}) => {
  const [editPetServiceOpen, setEditPetServiceOpen] = useState(false);
  const [openBreakdown, setOpenBreakdown] = useState({});
  // ── Service price breakdown popover (mirrors GroomingServiceCheckinCard) ──
  const [showBreakdown, setShowBreakdown] = useState(false);
  const breakdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (breakdownRef.current && !breakdownRef.current.contains(event.target)) {
        setShowBreakdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addonsTotal = addons.reduce(
    (sum, a) => sum + parseFloat(a.addon_price || a.price || 0),
    0,
  );
  const productsTotal = products.reduce((sum, p) => {
    const effectivePrice = parseFloat(
      p.effective_sale_price ||
        p.unit_price ||
        p.price ||
        p.sale_price ||
        p.mrp ||
        p.total_price / (p.quantity || 1) ||
        0,
    );
    const quantity = p.quantity || 1;
    return sum + (isNaN(effectivePrice) ? 0 : effectivePrice * quantity);
  }, 0);
  const chargesTotal = otherCharges.reduce(
    (sum, c) => sum + parseFloat(c.amount || 0),
    0,
  );
  const totalCharges = addonsTotal + productsTotal + chargesTotal;

  const canEditService = !readOnly && !!(rawData && orderId);
  const isBoarding =
    String(serviceCategory || "").toLowerCase() === "boarding" ||
    String(rawData?.item_type || "").toLowerCase() === "boarding" ||
    String(rawData?.service_type_name || "").toLowerCase() === "boarding" ||
    String(boardingOrderItem?.item_type || "").toLowerCase() === "boarding";

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="px-2 py-3">
        {/* Header row: category | date | time — left; action buttons — right */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-sm font-bold text-gray-900">
            {serviceCategory}

            {isBoarding ? (
              <>
                {(boardingOrderItem?.start_date ||
                  serviceDetails.checkInDate) && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">|</span> Check
                    In:{" "}
                    {formatDate(
                      boardingOrderItem?.start_date ||
                        serviceDetails.checkInDate,
                    )}
                  </>
                )}

                {(boardingOrderItem?.end_date ||
                  serviceDetails.checkOutDate) && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">|</span> Check
                    Out:{" "}
                    {formatDate(
                      boardingOrderItem?.end_date ||
                        serviceDetails.checkOutDate,
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                {serviceDetails.date && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">
                      |
                    </span> Date: {formatDate(serviceDetails.date)}
                  </>
                )}

                {serviceDetails.time && (
                  <>
                    {" "}
                    <span className="text-gray-400 font-normal">
                      |
                    </span> Time: {serviceDetails.time}
                  </>
                )}
              </>
            )}
          </h3>
          <div className="flex items-center gap-4 flex-wrap">
            {!readOnly && (
              <>
                <button
                  onClick={onOpenProductsDrawer}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-xs sm:text-sm transition-colors flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Products
                </button>
                <button
                  onClick={onOpenAddonsDrawer}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-xs sm:text-sm transition-colors flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" />
                  Add Add-ons
                </button>
                <button
                  onClick={onOpenOtherChargesDrawer}
                  className="text-teal-600 hover:text-teal-700 font-semibold text-xs sm:text-sm transition-colors flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" />
                  Other Charges
                </button>
              </>
            )}
          </div>
        </div>

        {/* Service name + price + edit pen */}
        <div className="flex items-center justify-between">
          <span className="text-gray-900 font-medium flex items-center gap-4">
            {serviceDetails.name || "No Service"}
            {/* {canEditService && (
              <button
                onClick={() => setEditPetServiceOpen(true)}
                className="text-blue-500 hover:text-indigo-600 transition-colors"
                title="Edit Service"
              >
                <FaPen className="w-3.5 h-3.5" />
              </button>
            )} */}
          </span>
          <div className="flex items-center gap-2 relative" ref={breakdownRef}>
            {/* Info icon – toggles breakdown popover (only when a price exists) */}
            {Number(serviceDetails.price || 0) > 0 && (
              <button
                onClick={() => setShowBreakdown((prev) => !prev)}
                className="text-gray-400 hover:text-gray-600 transition"
                title="View price breakdown"
              >
                <FaInfoCircle size={16} />
              </button>
            )}

            <span className="text-green-600 font-bold">
              ₹{Number(serviceDetails.price || 0).toFixed(0)}
            </span>

            {/* Breakdown Popover – same style as GroomingServiceCheckinCard */}
            {showBreakdown && Number(serviceDetails.price || 0) > 0 && (
              <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4 text-sm">
                <div className="font-semibold text-gray-800 mb-3 border-b pb-2">
                  Price Breakdown
                </div>
                <div className="space-y-1.5">
                  {/* Base price */}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Base Price</span>
                    <span>
                      ₹
                      {(
                        (serviceDetails.base_total ||
                          Number(serviceDetails.price || 0) -
                            (serviceDetails.gst_amount || 0)) -
                        ((serviceDetails.surchargesTotal ??
                          (serviceDetails.surchargeDetails ||
                            serviceDetails.price_surcharge ||
                            []
                          ).reduce((sum, s) => sum + (Number(s.price) || 0), 0)) * Number(serviceDetails?.quantity || 1))
                      ).toLocaleString()}
                    </span>
                  </div>

                  {/* Surcharges */}
                  {(serviceDetails.surchargeDetails ||
                    serviceDetails.price_surcharge ||
                    []
                  ).length > 0 && (
                    <div className="pl-3 border-l-2 border-gray-200 ml-1">
                      {(serviceDetails.surchargeDetails ||
                        serviceDetails.price_surcharge ||
                        []
                      ).map((s, i) => (
                        <div key={i} className="flex justify-between">
                          <span className="text-gray-500 text-xs capitalize">
                            {s.name?.replace(/_/g, " ") || "Surcharge"}
                          </span>
                          <span className="text-xs text-green-600">
                            +₹{(Number(s.price) * Number(serviceDetails?.quantity || 1))?.toLocaleString() || 0}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between font-medium text-xs border-t border-gray-100 pt-1 mt-1">
                        <span>Total surcharges</span>
                        <span className="text-green-600">
                          +₹
                          {((
                            serviceDetails.surchargesTotal ??
                            (serviceDetails.surchargeDetails ||
                              serviceDetails.price_surcharge ||
                              []
                            ).reduce((sum, s) => sum + (Number(s.price) || 0), 0)
                          ) * Number(serviceDetails?.quantity || 1)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Discount */}
                  {serviceDetails.discount_amount > 0 && (
                    <div className="flex justify-between text-red-500">
                      <span>Discount</span>
                      <span>
                        -₹{serviceDetails.discount_amount?.toLocaleString() || 0}
                      </span>
                    </div>
                  )}

                  {/* Tax */}
                  {serviceDetails.gst_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Tax ({serviceDetails.tax_percentage || 0}%)
                      </span>
                      <span>₹{serviceDetails.gst_amount?.toLocaleString() || 0}</span>
                    </div>
                  )}

                  {/* Total */}
                  <div className="flex justify-between font-bold text-green-700 border-t pt-1.5 mt-1.5">
                    <span>Total</span>
                    <span>₹{Number(serviceDetails.price || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Duration • Staff */}
        {isBoarding ? (
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
            {(boardingOrderItem?.duration || serviceDetails.durationDays) && (
              <span>
                <span className="font-medium text-gray-700">Duration:</span>{" "}
                {boardingOrderItem?.duration || serviceDetails.durationDays}{" "}
                Days
              </span>
            )}
          </div>
        ) : (
          <p className="flex items-center flex-wrap text-sm text-gray-500 mt-1 gap-2">
            {serviceDetails.duration && <span>{serviceDetails.duration}</span>}

            {serviceDetails.duration && staffName && (
              <span className="text-gray-400">•</span>
            )}

            {staffName && <span>{staffName}</span>}
          </p>
        )}

        {/* ADD-ONS */}
        {addons.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Add-on Services
            </h4>
            <div className="space-y-2">
              {addons.map((addon, aIdx) => (
                <div
                  key={aIdx}
                  className="flex items-center justify-between p-2 bg-green-50 rounded-lg border border-green-100"
                >
                  <div className="flex items-center gap-2">
                    <FiCheck className="w-3 h-3 text-green-600" />
                    <span className="text-sm font-medium text-gray-800">
                      {addon.addon_name || addon.name || `Addon ${aIdx + 1}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">
                      ₹
                      {Number(addon.addon_price || addon.price || 0).toFixed(2)}
                    </span>
                    {!readOnly && (
                      <button
                        onClick={() => onDeleteAddon(aIdx)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove addon"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PRODUCTS */}
        {products.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Products</h4>
            <div className="space-y-2">
              {products.map((product, pIdx) => {
                // Get values from product
                const mrpPrice = parseFloat(
                  product.mrp_price || product.mrp || product.sale_price || 0,
                );

                const effectivePrice = parseFloat(
                  product.effective_sale_price ??
                    product.unit_price ??
                    product.price ??
                    product.sale_price ??
                    product.mrp ??
                    product.total_price / (product.quantity || 1) ??
                    0,
                );

                const gstSlab = parseFloat(
                  product.gst_slab ?? product.tax_percentage ?? 0,
                );

                const priceWithoutTax =
                  gstSlab > 0
                    ? effectivePrice / (1 + gstSlab / 100)
                    : effectivePrice;

                const gstAmount =
                  gstSlab > 0 ? effectivePrice - priceWithoutTax : 0;

                const quantity = product.quantity || 1;
                const totalEffective = effectivePrice * quantity;
                const totalWithoutTax = priceWithoutTax * quantity;
                const totalGst = gstAmount * quantity;

                // Breakdown values (quantity-multiplied where relevant)
                const unitDiscount = Math.max(mrpPrice - effectivePrice, 0);
                const totalDiscount = unitDiscount * quantity;

                const isOpen = !!openBreakdown[pIdx];

                return (
                  <div
                    key={pIdx}
                    className="relative flex flex-col p-2 pr-7 bg-blue-50 rounded-lg border border-blue-100"
                  >
                    {!readOnly && (
                      <button
                        onClick={() => onDeleteProduct(pIdx)}
                        className="absolute top-1 right-1 text-red-500 hover:text-red-700"
                        style={{ zIndex: 0 }}
                        title="Remove product"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    )}

                    {/* Main row */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-2 flex-col">
                        <span className="text-sm font-medium text-gray-800 flex items-center gap-1">
                          <span>{product.product_name || product.name}</span>
                          {gstSlab > 0 && (
                            <span className="italic text-xs">(gst {gstSlab}%)</span>
                          )}

                          {/* Description tooltip (kept) */}
                          <span className="relative inline-flex group">
                            <span className="cursor-help text-gray-500 hover:text-gray-700">
                              <FaInfoCircle className="text-sm text-gray-500 cursor-pointer" />
                            </span>
                            <div className="invisible absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 rounded bg-gray-800 px-3 py-2 text-sm text-white opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 w-72">
                              <div className="whitespace-normal">
                                <strong className="block mb-1">Product Details</strong>
                                {product.description && <div>{product.description}</div>}
                                {effectivePrice > 0 && (
                                  <div>
                                    Price: ₹{effectivePrice.toFixed(2)}
                                    {product.applied_discount && (
                                      <span className="italic text-sm ml-1">
                                        ({product.applied_discount.name}:{" "}
                                        {product.applied_discount.value}% off)
                                      </span>
                                    )}
                                  </div>
                                )}
                                {mrpPrice > 0 && (
                                  <div>MRP: ₹{mrpPrice.toFixed(2)}</div>
                                )}
                                {product.batches?.length > 0 && (
                                  <div>Batch: {product.batches[0]?.batch_number}</div>
                                )}
                              </div>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                            </div>
                          </span>
                        </span>
                      </div>

                      <div className="flex items-center gap-4">
                        {/* "i" info toggle — popover anchor */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenBreakdown((prev) => ({
                                ...prev,
                                [pIdx]: !prev[pIdx],
                              }));
                            }}
                            className="text-gray-400 hover:text-gray-600 transition"
                            aria-label={
                              isOpen ? "Hide price breakdown" : "Show price breakdown"
                            }
                          >
                            <FaInfoCircle size={16} />
                          </button>

                          {/* Floating breakdown popover */}
                          {isOpen && (
                            <>
                              {/* click-away backdrop */}
                              <div
                                className="fixed inset-0 z-20"
                                onClick={() =>
                                  setOpenBreakdown((prev) => ({
                                    ...prev,
                                    [pIdx]: false,
                                  }))
                                }
                              />
                              <div className="absolute right-0 top-7 z-30 w-52 p-2 bg-white rounded-md border border-blue-200 shadow-lg text-xs text-gray-700">
                                <div className="flex justify-between py-0.5">
                                  <span>MRP / unit</span>
                                  <span>₹{mrpPrice.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between py-0.5">
                                  <span>Total discount (×{quantity})</span>
                                  <span className="text-red-500">
                                    -₹{totalDiscount.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between py-0.5">
                                  <span>Tax amount (×{quantity})</span>
                                  <span className="text-gray-600">
                                    +₹{totalGst.toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between py-0.5 border-t border-gray-200 mt-1 pt-1 font-semibold">
                                  <span>Total</span>
                                  <span>₹{totalEffective.toFixed(2)}</span>
                                </div>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Quantity controls */}
                        <div className="flex items-center gap-2">
                          {!readOnly && quantity > 1 && (
                            <button
                              onClick={() => onDecrementProduct(pIdx)}
                              className="text-black-500 hover:text-black-700"
                              title="Decrease quantity"
                            >
                              <FiMinus className="w-4 h-4" />
                            </button>
                          )}
                          {!readOnly && quantity === 1 && (
                            <button
                              onClick={() => onDeleteProduct(pIdx)}
                              className="text-black-500 hover:text-black-700"
                              style={{ zIndex: 0 }}
                              title="Remove product"
                            >
                              <FiTrash2 className="w-3 h-3" />
                            </button>
                          )}
                          <span>{quantity}</span>
                          {!readOnly && (
                            <button
                              onClick={() => onIncrementProduct(pIdx)}
                              className="text-black-500 hover:text-black-700"
                              title="Increase quantity"
                            >
                              <FiPlus className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Final amount only */}
                        <div className="flex items-center gap-3">
                          <div className="text-green-600 font-bold min-w-[70px] text-right">
                            ₹{totalEffective.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* OTHER CHARGES */}
        {otherCharges.length > 0 && (
          <div className="mt-3">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Other Charges
            </h4>
            <div className="space-y-2">
              {otherCharges.map((charge, cIdx) => (
                <div
                  key={cIdx}
                  className="flex items-center justify-between p-2 bg-purple-50 rounded-lg border border-purple-100"
                >
                  <span className="text-sm font-medium text-gray-800">
                    {charge.description || charge.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-bold">
                      ₹{Number(charge.amount || 0).toFixed(2)}
                    </span>
                    {!readOnly && (
                      <button
                        onClick={() => onDeleteOtherCharge(cIdx)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove charge"
                      >
                        <FiTrash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* {(addons.length > 0 ||
          products.length > 0 ||
          otherCharges.length > 0) && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
            <span className="text-sm font-bold text-slate-700">
              Additional charges total: ₹{totalCharges.toFixed(2)}
            </span>
          </div>
        )} */}
      </div>

      {/* Self-hosted edit drawer — no parent wiring required beyond passing data in */}
      {editPetServiceOpen && (
        <PetServiceCategoryDrawer
          isOpen={editPetServiceOpen}
          onClose={() => setEditPetServiceOpen(false)}
          onUpdate={(updated) => {
            setEditPetServiceOpen(false);
            if (updated && (updated.pet_name || updated.service_name)) {
              onServiceUpdated?.(updated);
            }
          }}
          clientId={clientId}
          clientName={clientName}
          clientNumber={clientPhone}
          itemData={{
            ...rawData,
            ref_id: rawData?.ref_id,
            vendor_pet_id: vendorPetId,
            service_id: rawData?.service_id,
            service_type_name:
              rawData?.service_type_name || rawData?.type || "grooming",
            order_id: orderId,
          }}
          orderId={orderId}
          editMode={true}
          isServiceEditMode={true}
          saleType="appointment"
        />
      )}
    </div>
  );
};

export default ServiceDetailsCard;
